import { ReactNode, useEffect, useState } from "react";
import {
  Box,
  ChevronLeft,
  ChevronRight,
  File as FileIcon,
  FileImage,
  FileSpreadsheet,
  FileText,
  Folder,
  Home,
  Loader,
  Pin,
  PinOff,
  Search,
} from "lucide-react";
import {
  HydratedOrganizationFile,
  ORGANIZATION_FILE_ENTRY_TYPE,
} from "@forgepoint/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { AssetFileViewer } from "@/modules/technicalDesigns/AssetFileViewer";
import { useFilesStore } from "@/modules/files/filesStore";
import { useViewContextStore } from "@/store/viewContextStore";
import { useDebounce } from "@/lib/hooks";
import { cn } from "@/lib/utils";

interface ItemVaultTabProps {
  linkedFiles: HydratedOrganizationFile[];
  onPin: (file: HydratedOrganizationFile) => void;
  onUnpin: (fileId: string) => void;
}

type FolderCrumb = { id: string; name: string };

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_PAGE_SIZE = 50;

/**
 * Vault tab for the item editor: the files linked to this part on top, and a
 * vault browser below — folder drill-down with a breadcrumb trail, or flat
 * search results across the whole vault while a query is typed. Clicking a
 * file opens it in an inline viewer within the tab; Back returns to the list.
 */
export function ItemVaultTab({
  linkedFiles,
  onPin,
  onUnpin,
}: ItemVaultTabProps) {
  const [search, setSearch] = useState("");
  const [folderTrail, setFolderTrail] = useState<FolderCrumb[]>([]);
  const [browsedIds, setBrowsedIds] = useState<string[] | null>(null);
  const [searchTotal, setSearchTotal] = useState(0);
  const [viewedFile, setViewedFile] = useState<HydratedOrganizationFile | null>(
    null,
  );
  const files = useFilesStore((state) => state.files);
  const fetchFiles = useFilesStore((state) => state.fetchFiles);
  const mergeView = useViewContextStore((state) => state.mergeView);

  // Reports the opened vault file into the shared view snapshot; the cleanup
  // covers closing the viewer, switching files, and the tab unmounting.
  useEffect(() => {
    if (!viewedFile) {
      return;
    }
    mergeView({ vaultFileId: viewedFile.id, vaultFileName: viewedFile.name });
    return () =>
      mergeView({ vaultFileId: undefined, vaultFileName: undefined });
  }, [viewedFile, mergeView]);

  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
  const currentFolderId = folderTrail.at(-1)?.id ?? null;
  const isSearching = debouncedSearch.length > 0;

  const loadSearchPage = async (offset: number) => {
    if (offset === 0) {
      setBrowsedIds(null);
    }
    const ids = await fetchFiles({
      search: debouncedSearch,
      excludeFolders: true,
      limit: SEARCH_PAGE_SIZE,
      offset: offset || undefined,
    });
    setBrowsedIds((prev) => (offset === 0 ? ids : [...(prev ?? []), ...ids]));
    setSearchTotal(useFilesStore.getState().total);
  };

  useEffect(() => {
    const loadEntries = async () => {
      if (isSearching) {
        await loadSearchPage(0);
        return;
      }
      setBrowsedIds(null);
      const ids = await fetchFiles({ parentFolderId: currentFolderId });
      setBrowsedIds(ids);
    };
    loadEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, currentFolderId, fetchFiles]);

  const linkedIds = new Set(linkedFiles.map((file) => file.id));
  const entries = (browsedIds ?? [])
    .map((id) => files.get(id))
    .filter((entry): entry is HydratedOrganizationFile => !!entry)
    .filter((entry) => !linkedIds.has(entry.id));
  const folders = entries.filter(
    (entry) => entry.type === ORGANIZATION_FILE_ENTRY_TYPE.FOLDER,
  );
  const browsedFiles = entries.filter(
    (entry) => entry.type === ORGANIZATION_FILE_ENTRY_TYPE.FILE,
  );
  const isLoading = browsedIds === null;

  if (viewedFile) {
    return (
      <VaultFileViewer
        file={viewedFile}
        isLinked={linkedIds.has(viewedFile.id)}
        onBack={() => setViewedFile(null)}
        onPin={onPin}
        onUnpin={onUnpin}
      />
    );
  }

  return (
    <div className="h-full w-full flex flex-col overflow-y-auto px-4 pb-3">
      <VaultSection
        title="Files for This Part"
        count={linkedFiles.length}
        accent
      >
        {linkedFiles.length === 0 && (
          <p className="text-sm text-muted-foreground leading-relaxed py-1.5 pr-2">
            Nothing linked yet. Frank links the files he uses when quoting this
            part — or pin any file from the vault below.
          </p>
        )}
        {linkedFiles.map((file) => (
          <FileRow
            key={file.id}
            file={file}
            onOpenFile={setViewedFile}
            actionIcon={<PinOff className="h-3.5 w-3.5" />}
            actionLabel={`Unpin ${file.name}`}
            onAction={() => onUnpin(file.id)}
          />
        ))}
      </VaultSection>

      <VaultSection title="Vault" count={isSearching ? searchTotal : undefined}>
        <div className="relative mb-2">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search the whole vault..."
            className="pl-9 h-9 text-sm bg-muted/40 border-transparent focus-visible:bg-background focus-visible:border-input"
          />
        </div>

        {!isSearching && (
          <div className="flex items-center gap-1 flex-wrap px-1 pb-2 text-xs text-muted-foreground">
            <button
              type="button"
              aria-label="Vault root"
              className={cn(
                "hover:text-foreground transition-colors",
                folderTrail.length === 0 && "text-foreground",
              )}
              onClick={() => setFolderTrail([])}
            >
              <Home className="h-3.5 w-3.5" />
            </button>
            {folderTrail.map((crumb, index) => (
              <span key={crumb.id} className="flex items-center gap-1">
                <ChevronRight className="h-3 w-3 opacity-50" />
                <button
                  type="button"
                  className={cn(
                    "hover:text-foreground transition-colors truncate max-w-36",
                    index === folderTrail.length - 1 &&
                      "font-medium text-foreground",
                  )}
                  onClick={() =>
                    setFolderTrail((trail) => trail.slice(0, index + 1))
                  }
                >
                  {crumb.name}
                </button>
              </span>
            ))}
          </div>
        )}

        {isLoading && (
          <div className="flex justify-center py-3">
            <Loader className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}

        {!isLoading && entries.length === 0 && (
          <p className="text-sm text-muted-foreground py-1.5 px-1">
            {isSearching
              ? "No files match your search."
              : folderTrail.length === 0
                ? "The vault is empty."
                : "This folder is empty."}
          </p>
        )}

        {!isSearching &&
          folders.map((folder) => (
            <FolderRow
              key={folder.id}
              folder={folder}
              onDrill={() =>
                setFolderTrail((trail) => [
                  ...trail,
                  { id: folder.id, name: folder.name ?? "Folder" },
                ])
              }
            />
          ))}

        {browsedFiles.map((file) => (
          <FileRow
            key={file.id}
            file={file}
            onOpenFile={setViewedFile}
            showPath={isSearching}
            actionIcon={<Pin className="h-3.5 w-3.5" />}
            actionLabel={`Pin ${file.name}`}
            onAction={() => onPin(file)}
          />
        ))}

        {isSearching &&
          !isLoading &&
          (browsedIds?.length ?? 0) < searchTotal && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full h-8 text-sm text-muted-foreground"
              onClick={() => loadSearchPage(browsedIds?.length ?? 0)}
            >
              Show more
            </Button>
          )}
      </VaultSection>
    </div>
  );
}

interface VaultFileViewerProps {
  file: HydratedOrganizationFile;
  isLinked: boolean;
  onBack: () => void;
  onPin: (file: HydratedOrganizationFile) => void;
  onUnpin: (fileId: string) => void;
}

/**
 * Inline viewer for a vault file within the Vault tab: back bar with the file
 * name and a pin toggle on top, the asset preview filling the rest.
 */
function VaultFileViewer({
  file,
  isLinked,
  onBack,
  onPin,
  onUnpin,
}: VaultFileViewerProps) {
  const TypeIcon = fileTypeIcon(file.name);

  return (
    <div className="h-full w-full flex flex-col">
      <div className="flex items-center gap-2 px-3 py-2 border-b">
        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-1 px-2 shrink-0"
          onClick={onBack}
        >
          <ChevronLeft className="h-4 w-4" />
          Back
        </Button>
        <TypeIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="truncate min-w-0 flex-1 text-sm font-medium">
          {file.name}
        </span>
        <Button
          variant="ghost"
          size="sm"
          className={cn("h-8 gap-1.5 px-2 shrink-0", isLinked && "text-danger")}
          onClick={() => (isLinked ? onUnpin(file.id) : onPin(file))}
        >
          {isLinked ? (
            <PinOff className="h-3.5 w-3.5" />
          ) : (
            <Pin className="h-3.5 w-3.5" />
          )}
          {isLinked ? "Unpin" : "Pin to part"}
        </Button>
      </div>
      <div className="flex-1 min-h-0 overflow-auto p-4">
        {file.asset ? (
          <div className="w-full h-full">
            <AssetFileViewer asset={file.asset} />
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-8">
            No preview available for this file.
          </p>
        )}
      </div>
    </div>
  );
}

interface VaultSectionProps {
  title: string;
  count?: number;
  /** Tints the count badge with the tab's red accent. */
  accent?: boolean;
  children: ReactNode;
}

/** A collapsible titled section of the vault tab, open by default. */
function VaultSection({ title, count, accent, children }: VaultSectionProps) {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className="border-b py-3 last:border-b-0"
    >
      <CollapsibleTrigger className="flex items-center gap-1.5 w-full cursor-pointer select-none group/section">
        <ChevronRight
          className={cn(
            "h-3.5 w-3.5 text-muted-foreground transition-transform duration-200",
            isOpen && "rotate-90",
          )}
        />
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground group-hover/section:text-foreground transition-colors">
          {title}
        </h3>
        {count !== undefined && count > 0 && (
          <Badge
            variant="secondary"
            className={cn(
              "text-[11px] tabular-nums px-1.5 py-0",
              accent && "bg-danger/10 text-danger hover:bg-danger/10",
            )}
          >
            {count}
          </Badge>
        )}
      </CollapsibleTrigger>
      <CollapsibleContent className="animate-collapsible-down data-[state=closed]:animate-collapsible-up pt-2 space-y-px">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}

interface FolderRowProps {
  folder: HydratedOrganizationFile;
  onDrill: () => void;
}

/** A folder row in the browser: click drills into it. */
function FolderRow({ folder, onDrill }: FolderRowProps) {
  return (
    <div
      className="group flex items-center gap-2.5 px-2 h-9 rounded-md text-sm cursor-pointer hover:bg-danger/10 transition-colors"
      onClick={onDrill}
    >
      <Folder className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="truncate min-w-0 flex-1 font-medium">{folder.name}</span>
      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
  );
}

interface FileRowProps {
  file: HydratedOrganizationFile;
  onOpenFile: (file: HydratedOrganizationFile) => void;
  /** Shows the file's folder path under its name (search results). */
  showPath?: boolean;
  actionIcon?: ReactNode;
  actionLabel?: string;
  onAction?: () => void;
}

/** A single vault file row: click opens it in the tab's inline viewer. */
function FileRow({
  file,
  onOpenFile,
  showPath,
  actionIcon,
  actionLabel,
  onAction,
}: FileRowProps) {
  // Red hover is the vault tab's deliberate interaction language, not an
  // error state.
  const TypeIcon = fileTypeIcon(file.name);
  const folderPath = parentPath(file);

  return (
    <div
      className={cn(
        "group flex items-center gap-2.5 px-2 rounded-md text-sm cursor-pointer transition-colors hover:bg-danger/10",
        showPath && folderPath ? "py-1.5" : "h-9",
      )}
      onClick={() => onOpenFile(file)}
    >
      <TypeIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <div className="truncate">{file.name}</div>
        {showPath && folderPath && (
          <div className="truncate text-[11px] text-muted-foreground">
            {folderPath}
          </div>
        )}
      </div>
      {onAction && (
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0 opacity-0 group-hover:opacity-100"
          aria-label={actionLabel}
          onClick={(event) => {
            event.stopPropagation();
            onAction();
          }}
        >
          {actionIcon}
        </Button>
      )}
    </div>
  );
}

const FILE_TYPE_ICONS: Array<{
  extensions: string[];
  icon: typeof FileIcon;
}> = [
  { extensions: ["pdf", "doc", "docx", "txt", "md"], icon: FileText },
  { extensions: ["png", "jpg", "jpeg", "gif", "webp", "svg"], icon: FileImage },
  { extensions: ["xls", "xlsx", "csv"], icon: FileSpreadsheet },
  {
    extensions: ["step", "stp", "iges", "igs", "stl", "dwg", "dxf"],
    icon: Box,
  },
];

/** Picks a type-specific icon for a file name, falling back to a plain file. */
function fileTypeIcon(fileName: string | null): typeof FileIcon {
  const extension = fileName?.split(".").pop()?.toLowerCase() ?? "";
  return (
    FILE_TYPE_ICONS.find((group) => group.extensions.includes(extension))
      ?.icon ?? FileIcon
  );
}

/** The file's folder path without its own name, for search-result context. */
function parentPath(file: HydratedOrganizationFile): string | null {
  const path = file.path ?? "";
  const separatorIndex = path.lastIndexOf("/");
  if (separatorIndex <= 0) {
    return null;
  }
  return path.slice(0, separatorIndex).split("/").join(" / ");
}
